/**
 * Onboarding — FINALNY MODEL WYWIADU
 * P0 UX: Welcome screen, state retention, upload validation, funnel tracking, clarity layer, breed fallback
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, ArrowLeft, Sparkles, Check, Loader2,
  MapPin, Target, Eye, Mail, TrendingUp, Send,
  Dog, Scissors, Store, Briefcase, ChevronRight,
  Upload, ImageIcon, Rocket, Users, Star, ExternalLink, Copy,
  Home, Camera, AlertCircle, RefreshCw, Shield, Search
} from 'lucide-react';
import { API } from '@/utils/api';
import { useFunnelTracking } from '@/hooks/useFunnelTracking';
import { useTranslation } from 'react-i18next';

const PHASE = { WELCOME: 'welcome', INTERVIEW: 'interview', PROCESSING: 'processing', RESULTS: 'results' };

const STEP_ICONS = {
  dog: Dog, scissors: Scissors, store: Store, briefcase: Briefcase,
  mail: Mail, 'trending-up': TrendingUp, eye: Eye, home: Home, star: Star, users: Users,
};

const PROCESSING_STEPS = [
  { text: 'Analizuję Twoją ofertę', delay: 0 },
  { text: 'Dobieram grupy docelowe', delay: 1400 },
  { text: 'Przygotowuję post promocyjny', delay: 2800 },
  { text: 'Tworzę Twoją stronę', delay: 4200 },
  { text: 'Gotowe!', delay: 5600 },
];

/* Upload validation constants */
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_EXTENSIONS = '.jpg, .jpeg, .png, .webp';

/* Clarity layer — step context hints */
const STEP_CLARITY = {
  profil: { why: 'Dopasujemy cały wywiad do Ciebie', next: 'Opowiesz o swojej ofercie' },
  rasa: { why: 'Dobierzemy grupy i treści do Twojej rasy', next: 'Resztę ras dodasz później' },
  nazwa: { why: 'Tę nazwę zobaczą Twoi klienci', next: 'Określisz lokalizację' },
  lokalizacja: { why: 'Pokażemy ofertę osobom z Twojej okolicy', next: 'Dobierzemy grupę docelową' },
  grupa_docelowa: { why: 'Trafimy do właściwych osób', next: 'Określisz swoje atuty' },
  wyrozniki: { why: 'Użyjemy tego w postach', next: 'Powiesz na czym Ci zależy' },
  intencja: { why: 'Dopasujemy sposób promocji', next: 'Sprawdzimy dostępność' },
  dostepnosc: { why: 'To zwiększa szansę na zapytania', next: 'Dodasz zdjęcia' },
  zdjecia: { why: 'Zdjęcia zwiększają zaufanie i liczbę zapytań', next: 'Określisz styl rozmowy' },
  styl_odpowiedzi: { why: 'Przygotujemy gotowe odpowiedzi', next: 'Opiszesz ofertę' },
  custom_description: { why: 'Dzięki temu posty będą bardziej autentyczne', next: 'Określisz wartość dla klienta' },
  wartosc_dla_klienta: { why: 'Posty sprzedażowe trafią w punkt', next: 'Generujemy Twój post!' },
  oferta: { why: 'Dobierzemy odpowiednie treści', next: 'Podasz nazwę firmy' },
  usluga: { why: 'Na tej podstawie stworzymy post', next: 'Określisz lokalizację' },
  klienci: { why: 'Trafimy do właściwych osób', next: 'Określisz swoje atuty' },
};

/* ────────────────────────── GRADIENT BGs per step ──────────────────────── */
const STEP_GRADIENTS = [
  'from-orange-500/8 via-transparent to-transparent',
  'from-blue-500/8 via-transparent to-transparent',
  'from-emerald-500/8 via-transparent to-transparent',
  'from-violet-500/8 via-transparent to-transparent',
  'from-rose-500/8 via-transparent to-transparent',
  'from-amber-500/8 via-transparent to-transparent',
];

const ACCENT_COLORS = ['#F47A3B', '#3B82F6', '#10B981', '#8B5CF6', '#F43F5E', '#F59E0B'];

export default function Onboarding() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { trackStart, trackStep, trackComplete, trackError } = useFunnelTracking('onboarding');

  const [phase, setPhase] = useState(PHASE.WELCOME);
  const [currentStep, setCurrentStep] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [totalSteps, setTotalSteps] = useState(6);
  const [textInput, setTextInput] = useState('');
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [aiFeedback, setAiFeedback] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [answers, setAnswers] = useState({});
  const [summary, setSummary] = useState('');
  const [processingStep, setProcessingStep] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState(1);
  const [firstPost, setFirstPost] = useState(null);
  const [recommendedGroups, setRecommendedGroups] = useState([]);
  const [postCopied, setPostCopied] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [breedSearch, setBreedSearch] = useState('');
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const multiAutoRef = useRef(null);

  /* ── P0.2: Autosave to localStorage ── */
  const AUTOSAVE_KEY = `apisai_onboarding_${user?.id || 'anon'}`;
  
  const saveToLocal = useCallback((newAnswers) => {
    try {
      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(newAnswers));
    } catch { /* quota exceeded — ignore */ }
  }, [AUTOSAVE_KEY]);

  const loadFromLocal = useCallback(() => {
    try {
      const saved = localStorage.getItem(AUTOSAVE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  }, [AUTOSAVE_KEY]);

  useEffect(() => { checkStatus(); }, []);

  const checkStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/interview/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.status === 'completed') {
        navigate('/panel');
        return;
      }
      if (res.data.status === 'in_progress' && res.data.step_index > 0) {
        const serverAnswers = res.data.answers || {};
        const localAnswers = loadFromLocal();
        // Merge: server is source of truth, local fills gaps
        const merged = { ...localAnswers, ...serverAnswers };
        setAnswers(merged);
        await startInterview();
        return;
      }
      // Load local answers even if not in_progress (partial save)
      const localAnswers = loadFromLocal();
      if (localAnswers && Object.keys(localAnswers).length > 0) {
        setAnswers(localAnswers);
      }
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const startInterview = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/interview/start`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCurrentStep(res.data.current_step);
      setStepIndex(res.data.step_index);
      setTotalSteps(res.data.total_steps);
      const serverAnswers = res.data.answers_so_far || {};
      setAnswers(prev => ({ ...prev, ...serverAnswers }));
      saveToLocal({ ...answers, ...serverAnswers });
      setPhase(PHASE.INTERVIEW);
      setLoading(false);
      trackStart({ profil: serverAnswers.profil || null });
      setTimeout(() => inputRef.current?.focus(), 400);
    } catch {
      toast.error('Nie udało się rozpocząć wywiadu');
      setLoading(false);
    }
  };

  const submitAnswer = async (value) => {
    if (submitting) return;
    setSubmitting(true);
    setShowFeedback(false);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/interview/answer`, {
        step_key: currentStep.key,
        answer: value,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data;
      setAiFeedback(data.ai_feedback);
      setShowFeedback(true);
      const newAnswers = { ...answers, [currentStep.key]: value };
      setAnswers(newAnswers);
      saveToLocal(newAnswers);

      // Funnel tracking
      trackStep(`step_${currentStep.key}_completed`, currentStep.question, {
        step_key: currentStep.key,
        step_index: stepIndex,
      });

      setTimeout(() => {
        if (data.is_complete) {
          setSummary(data.summary);
          trackComplete({ total_steps: totalSteps });
          startProcessing();
        } else {
          setDirection(1);
          setCurrentStep(data.next_step);
          setStepIndex(data.step_index);
          if (data.total_steps) setTotalSteps(data.total_steps);
          setTextInput('');
          setSelectedChoices([]);
          setSelectedChoice(null);
          setUploadedPhotos([]);
          setUploadError(null);
          setBreedSearch('');
          if (multiAutoRef.current) clearTimeout(multiAutoRef.current);
          setShowFeedback(false);
          setTimeout(() => inputRef.current?.focus(), 300);
        }
        setSubmitting(false);
      }, 1200);
    } catch {
      toast.error('Coś poszło nie tak');
      trackError(`step_${currentStep?.key}`, 'submit_error');
      setSubmitting(false);
    }
  };

  const handleChoiceSelect = (value) => {
    submitAnswer(value);
  };

  const handleMultiChoiceToggle = (value) => {
    setSelectedChoices(prev => {
      const next = prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value];
      // Auto-submit after 1.5s of inactivity if at least 1 selected
      if (multiAutoRef.current) clearTimeout(multiAutoRef.current);
      if (next.length > 0) {
        multiAutoRef.current = setTimeout(() => {
          submitAnswer(next.join(', '));
        }, 1500);
      }
      return next;
    });
  };

  const handleMultiChoiceSubmit = () => {
    if (multiAutoRef.current) clearTimeout(multiAutoRef.current);
    if (selectedChoices.length === 0) return;
    submitAnswer(selectedChoices.join(', '));
  };

  const handleSuggestionClick = (suggestion) => {
    setTextInput(suggestion);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleTextSubmit = () => {
    if (!textInput.trim()) return;
    submitAnswer(textInput.trim());
  };

  /* ── P0.3: Photo upload with validation, errors, retry ── */
  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return { valid: false, error: `Nieobsługiwany format: ${file.name}. Użyj JPG, PNG lub WebP.` };
    }
    if (file.size > MAX_FILE_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      return { valid: false, error: `Zdjęcie za duże (${sizeMB}MB). Maksymalnie 5MB.` };
    }
    return { valid: true };
  };

  const lastFailedFilesRef = useRef([]);

  const handlePhotoUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploadError(null);
    
    // Validate all files first
    const validFiles = [];
    for (const file of Array.from(files)) {
      const validation = validateFile(file);
      if (!validation.valid) {
        setUploadError(validation.error);
        trackError('photo_upload', 'validation_error', { error: validation.error, file_name: file.name });
        toast.error(validation.error);
        return;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    lastFailedFilesRef.current = validFiles;
    const token = localStorage.getItem('token');
    const urls = [...uploadedPhotos];

    try {
      for (let i = 0; i < validFiles.length; i++) {
        setUploadProgress(Math.round(((i) / validFiles.length) * 100));
        const formData = new FormData();
        formData.append('file', validFiles[i]);
        const res = await axios.post(`${API}/media-library/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        if (res.data?.file_url) urls.push(res.data.file_url);
        setUploadProgress(Math.round(((i + 1) / validFiles.length) * 100));
      }
      setUploadedPhotos(urls);
      setUploadError(null);
      toast.success(`Dodano ${validFiles.length} ${validFiles.length === 1 ? 'zdjęcie' : 'zdjęć'}`);
    } catch (err) {
      const errorMsg = 'Błąd przesyłania — spróbuj ponownie';
      setUploadError(errorMsg);
      trackError('photo_upload', 'upload_failed', { error: err.message });
      toast.error(errorMsg);
    }
    setUploading(false);
    setUploadProgress(0);
  };

  const handlePhotoRetry = () => {
    if (lastFailedFilesRef.current.length > 0) {
      handlePhotoUpload(lastFailedFilesRef.current);
    } else {
      fileInputRef.current?.click();
    }
  };

  const handlePhotoSubmit = () => {
    if (uploadedPhotos.length === 0) return;
    submitAnswer(uploadedPhotos);
  };

  /* ── P0.2: Helper to restore input state from a previous answer ── */
  const restoreInputFromAnswer = (step, answer) => {
    if (!step || answer === undefined || answer === null) {
      setTextInput('');
      setSelectedChoices([]);
      setSelectedChoice(null);
      setUploadedPhotos([]);
      setBreedSearch('');
      return;
    }

    const type = step.type;
    if (type === 'text' || type === 'textarea' || type === 'text_with_suggestions') {
      setTextInput(typeof answer === 'string' ? answer : '');
      setSelectedChoices([]);
      setSelectedChoice(null);
    } else if (type === 'choice' || type === 'hero_choice') {
      setSelectedChoice(typeof answer === 'string' ? answer : null);
      setTextInput('');
      setSelectedChoices([]);
    } else if (type === 'multi_choice') {
      const choices = typeof answer === 'string' ? answer.split(', ') : (Array.isArray(answer) ? answer : []);
      setSelectedChoices(choices);
      setTextInput('');
      setSelectedChoice(null);
    } else if (type === 'choice_or_custom') {
      // Check if it was a custom value or a preset option
      const isPreset = step.options?.some(o => o.value === answer);
      if (isPreset) {
        setSelectedChoice(answer);
        setTextInput('');
      } else {
        setTextInput(typeof answer === 'string' ? answer : '');
        setSelectedChoice(null);
      }
      setSelectedChoices([]);
      setBreedSearch('');
    } else if (type === 'photo_upload') {
      setUploadedPhotos(Array.isArray(answer) ? answer : []);
      setTextInput('');
      setSelectedChoices([]);
    } else {
      setTextInput('');
      setSelectedChoices([]);
      setSelectedChoice(null);
    }
  };

  const goBack = async () => {
    if (stepIndex <= 1 || submitting) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/interview/go-back`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data;
      if (data.step) {
        setDirection(-1);
        setCurrentStep(data.step);
        setStepIndex(data.step_index);
        if (data.total_steps) setTotalSteps(data.total_steps);

        // P0.2: Restore the previous answer instead of clearing
        restoreInputFromAnswer(data.step, data.previous_answer);
        
        setShowFeedback(false);
        setAiFeedback('');
        setUploadError(null);
        toast.success('Dane zachowane', { duration: 1500 });
      }
    } catch {
      toast.error('Nie udało się cofnąć');
    }
  };

  const startProcessing = () => {
    setPhase(PHASE.PROCESSING);
    setProcessingStep(-1);
    PROCESSING_STEPS.forEach((_, idx) => {
      setTimeout(() => setProcessingStep(idx), PROCESSING_STEPS[idx].delay + 400);
    });
    // Fetch first post in parallel with animation
    fetchFirstPost();
  };

  const fetchFirstPost = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/interview/generate-first-post`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFirstPost(res.data.post);
      setRecommendedGroups(res.data.groups || []);
      setSummary(res.data.summary || '');
      // Wait for animation to finish, then show results
      setTimeout(() => setPhase(PHASE.RESULTS), 1500);
    } catch (e) {
      console.error('First post error:', e);
      // Fallback — still show results
      setTimeout(() => setPhase(PHASE.RESULTS), 2000);
    }
  };

  const copyPostText = () => {
    if (firstPost?.text) {
      navigator.clipboard.writeText(firstPost.text);
      setPostCopied(true);
      toast.success('Post skopiowany!');
      setTimeout(() => setPostCopied(false), 2000);
    }
  };

  const accentColor = ACCENT_COLORS[stepIndex % ACCENT_COLORS.length];
  const gradient = STEP_GRADIENTS[stepIndex % STEP_GRADIENTS.length];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A12] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F47A3B]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A12] flex flex-col overflow-hidden relative">
      {/* Ambient gradient */}
      <div className={`fixed inset-0 bg-gradient-to-br ${gradient} pointer-events-none transition-all duration-1000`} />
      {/* Subtle noise texture */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(2px 2px at 20px 30px, white, transparent),
                         radial-gradient(1px 1px at 90px 40px, white, transparent)`,
        backgroundSize: '200px 150px'
      }} />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-[#F47A3B] flex items-center justify-center shadow-lg shadow-orange-500/20">
            <span className="text-white font-bold text-sm">A</span>
          </div>
          <span className="font-bold text-lg text-white/90 tracking-tight">ApisAI</span>
        </div>

        {phase === PHASE.INTERVIEW && (
          <div className="flex items-center gap-3">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className="transition-all duration-500"
                style={{
                  width: i === stepIndex ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: i < stepIndex ? '#10B981' : i === stepIndex ? accentColor : 'rgba(255,255,255,0.15)',
                }}
              />
            ))}
          </div>
        )}

        {phase === PHASE.INTERVIEW && (
          <div className="flex items-center gap-4">
            {stepIndex >= 1 && (
              <button
                onClick={goBack}
                className="flex items-center gap-1.5 text-sm text-white/30 hover:text-white/60 transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Wstecz
              </button>
            )}
            <span className="text-sm font-medium text-white/40">
              {stepIndex + 1} z {totalSteps}
            </span>
          </div>
        )}
      </header>

      {/* Main */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 pb-20">
        <AnimatePresence mode="wait" custom={direction}>

          {/* ═══ WELCOME ═══ */}
          {phase === PHASE.WELCOME && (
            <motion.div
              key="welcome"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-lg w-full text-center"
              data-testid="onboarding-welcome"
            >
              {/* Floating icon */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                className="w-20 h-20 mx-auto mb-8 rounded-3xl bg-gradient-to-br from-[#F47A3B] to-[#F9A66C] flex items-center justify-center shadow-2xl shadow-orange-500/30"
              >
                <Sparkles className="w-10 h-10 text-white" />
              </motion.div>

              <p className="text-base text-white/40 mb-3 font-medium">
                Nie masz strony? Spokojnie.
              </p>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-4 leading-tight" data-testid="welcome-heading">
                Zrobimy razem post i przygotujemy miejsce, gdzie klienci będą mogli się odezwać
              </h1>
              <p className="text-base text-white/50 mb-10">
                Nie musisz nic umieć. Odpowiadasz klikając, a my ogarniamy resztę.
              </p>

              {/* 3 steps preview */}
              <div className="flex items-center justify-center gap-2 mb-10">
                {[
                  { label: 'Wywiad', active: true, desc: '2 min' },
                  { label: 'Analiza', desc: 'automatyczna' },
                  { label: 'Post', desc: 'gotowy!' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="text-center">
                      <div
                        className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${
                          item.active
                            ? 'bg-[#F47A3B] text-white shadow-lg shadow-orange-500/25'
                            : 'bg-white/10 text-white/40'
                        }`}
                      >
                        {item.label}
                      </div>
                      <p className="text-[10px] text-white/25 mt-1">{item.desc}</p>
                    </div>
                    {i < 2 && <ChevronRight className="w-3.5 h-3.5 text-white/20 mt-[-12px]" />}
                  </div>
                ))}
              </div>

              {/* Trust indicators */}
              <div className="flex items-center justify-center gap-5 mb-8">
                {[
                  { icon: Shield, text: 'Bez wiedzy technicznej' },
                  { icon: Camera, text: 'Zdjęcia opcjonalne' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-white/30 text-xs">
                    <item.icon className="w-3.5 h-3.5" />
                    {item.text}
                  </div>
                ))}
              </div>

              <Button
                data-testid="start-interview-btn"
                onClick={startInterview}
                className="h-14 px-10 text-lg font-bold rounded-2xl text-white bg-[#F47A3B] hover:bg-[#E06A2E] shadow-xl shadow-orange-500/25 hover:shadow-2xl hover:shadow-orange-500/35 transition-all duration-300 group"
              >
                Zacznij
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>

              <p className="text-xs text-white/30 mt-5">
                Zajmie to tylko 2 minuty
              </p>
            </motion.div>
          )}

          {/* ═══ INTERVIEW ═══ */}
          {phase === PHASE.INTERVIEW && currentStep && (
            <motion.div
              key={`step-${currentStep.key}`}
              custom={direction}
              initial={{ opacity: 0, x: direction * 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction * -60 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-xl w-full"
              data-testid="onboarding-interview"
            >
              {/* AI Feedback toast */}
              <AnimatePresence>
                {showFeedback && aiFeedback && (
                  <motion.div
                    initial={{ opacity: 0, y: -15, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mb-6 flex items-start gap-3 p-4 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10"
                  >
                    <div className="w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ background: accentColor }}>
                      <Sparkles className="w-3.5 h-3.5 text-white" />
                    </div>
                    <p className="text-sm text-white/60 pt-0.5">{aiFeedback}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Question */}
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-2xl sm:text-3xl font-extrabold text-white mb-2 leading-snug"
                data-testid="interview-question"
              >
                {currentStep.question}
              </motion.h2>

              {currentStep.subtitle && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.15 }}
                  className="text-base text-white/40 mb-4"
                >
                  {currentStep.subtitle}
                </motion.p>
              )}

              {/* ── P0.5: Clarity Layer — why this step + what's next ── */}
              {STEP_CLARITY[currentStep.key] && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center gap-4 mb-6 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06]"
                  data-testid="clarity-layer"
                >
                  <div className="flex items-center gap-1.5 text-[11px] text-white/30">
                    <span className="w-1 h-1 rounded-full bg-emerald-500" />
                    {STEP_CLARITY[currentStep.key].why}
                  </div>
                  <div className="h-3 w-px bg-white/10" />
                  <div className="flex items-center gap-1.5 text-[11px] text-white/25">
                    <ArrowRight className="w-2.5 h-2.5" />
                    {STEP_CLARITY[currentStep.key].next}
                  </div>
                </motion.div>
              )}

              {/* ── HERO CHOICE TYPE (Hodowca vs Firma) ── */}
              {currentStep.type === 'hero_choice' && currentStep.options && (
                <div className="grid grid-cols-2 gap-4" data-testid="interview-hero-choices">
                  {currentStep.options.map((opt, i) => {
                    const Icon = STEP_ICONS[opt.icon] || Target;
                    return (
                      <motion.button
                        key={opt.value}
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 0.15 + i * 0.1, type: 'spring' }}
                        whileHover={{ scale: 1.03, y: -3 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => handleChoiceSelect(opt.value)}
                        disabled={submitting}
                        className="relative p-6 rounded-2xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 transition-all text-center group overflow-hidden"
                        data-testid={`choice-${opt.value}`}
                      >
                        {/* Glow on hover */}
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
                          background: `radial-gradient(circle at 50% 50%, ${accentColor}10, transparent 70%)`
                        }} />
                        <div
                          className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center transition-all group-hover:scale-110"
                          style={{ background: accentColor + '20', color: accentColor }}
                        >
                          <Icon className="w-8 h-8" />
                        </div>
                        <span className="font-bold text-white text-lg block mb-1">{opt.label}</span>
                        {opt.desc && <span className="text-sm text-white/40 leading-snug block">{opt.desc}</span>}
                      </motion.button>
                    );
                  })}
                </div>
              )}

              {/* ── CHOICE TYPE (auto-submit on click) ── */}
              {currentStep.type === 'choice' && currentStep.options && (
                <div className="space-y-2.5" data-testid="interview-choices">
                  {currentStep.options.map((opt, i) => {
                    const Icon = STEP_ICONS[opt.icon] || Target;
                    return (
                      <motion.button
                        key={opt.value}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + i * 0.06 }}
                        whileHover={{ scale: 1.01, y: -1 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => handleChoiceSelect(opt.value)}
                        disabled={submitting}
                        className="w-full text-left p-4 rounded-2xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 transition-all flex items-center gap-4 group"
                        data-testid={`choice-${opt.value}`}
                      >
                        <div
                          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-105"
                          style={{ background: accentColor + '20', color: accentColor }}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="font-semibold text-white block">{opt.label}</span>
                          {opt.desc && <span className="text-sm text-white/40">{opt.desc}</span>}
                        </div>
                        <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-white/50 group-hover:translate-x-0.5 transition-all" />
                      </motion.button>
                    );
                  })}
                </div>
              )}

              {/* ── MULTI CHOICE TYPE ── */}
              {currentStep.type === 'multi_choice' && currentStep.options && (
                <>
                  {currentStep.strongly_recommended && (
                    <div className="flex items-center gap-2 mb-3 px-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#F47A3B] animate-pulse" />
                      <span className="text-[11px] font-medium text-[#F47A3B]/80">Mocno rekomendowane — pomoże tworzyć mocniejsze posty</span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2.5 mb-4" data-testid="interview-multi-choices">
                    {currentStep.options.map((opt, i) => {
                      const isSelected = selectedChoices.includes(opt.value);
                      return (
                        <motion.button
                          key={opt.value}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.08 + i * 0.04 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleMultiChoiceToggle(opt.value)}
                          disabled={submitting}
                          className={`px-4 py-2.5 rounded-xl font-medium text-sm border transition-all ${
                            isSelected
                              ? 'text-white border-transparent'
                              : 'bg-white/[0.04] text-white/60 border-white/10 hover:bg-white/[0.08] hover:border-white/20'
                          }`}
                          style={isSelected ? {
                            background: accentColor,
                            borderColor: accentColor,
                            boxShadow: `0 4px 12px ${accentColor}40`,
                          } : {}}
                          data-testid={`multi-${opt.value}`}
                        >
                          {isSelected && <Check className="w-3.5 h-3.5 inline mr-1.5" />}
                          {opt.label}
                        </motion.button>
                      );
                    })}
                  </div>

                  {/* Custom text input for allow_custom */}
                  {currentStep.allow_custom && (
                    <div className="mb-4">
                      <input
                        ref={inputRef}
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && textInput.trim()) {
                            handleMultiChoiceToggle(textInput.trim());
                            setTextInput('');
                          }
                        }}
                        placeholder={currentStep.custom_placeholder || 'Inne — wpisz...'}
                        disabled={submitting}
                        className="w-full h-10 text-sm rounded-xl border border-white/10 bg-white/[0.04] px-4 focus:border-white/25 focus:bg-white/[0.06] focus:outline-none focus:ring-0 text-white placeholder:text-white/25 transition-all"
                        data-testid="multi-choice-custom-input"
                      />
                    </div>
                  )}

                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
                    <Button
                      onClick={handleMultiChoiceSubmit}
                      disabled={submitting || selectedChoices.length === 0}
                      className={`h-12 px-8 rounded-xl font-semibold text-white transition-all ${selectedChoices.length > 0 ? 'animate-pulse' : ''}`}
                      style={{
                        background: selectedChoices.length > 0 ? accentColor : 'rgba(255,255,255,0.1)',
                        boxShadow: selectedChoices.length > 0 ? `0 4px 16px ${accentColor}30` : 'none',
                      }}
                      data-testid="multi-choice-submit"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                        <>
                          Dalej ({selectedChoices.length})
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-white/30 mt-2">
                      Przejdzie dalej automatycznie lub kliknij Dalej
                    </p>
                  </motion.div>
                </>
              )}

              {/* ── CHOICE OR CUSTOM TYPE (click option = auto-advance / lub wpisz) ── */}
              {currentStep.type === 'choice_or_custom' && currentStep.options && (
                <div data-testid="interview-choice-or-custom">
                  {/* P0.6: Search/filter for long breed lists */}
                  {currentStep.options.length > 8 && (
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
                      <input
                        value={breedSearch}
                        onChange={(e) => setBreedSearch(e.target.value)}
                        placeholder="Szukaj rasy..."
                        className="w-full h-10 text-sm rounded-xl border border-white/10 bg-white/[0.04] pl-9 pr-4 focus:border-white/25 focus:bg-white/[0.06] focus:outline-none focus:ring-0 text-white placeholder:text-white/25 transition-all"
                        data-testid="breed-search-input"
                      />
                    </div>
                  )}
                  
                  {(() => {
                    const filteredOptions = breedSearch.trim()
                      ? currentStep.options.filter(o => o.label.toLowerCase().includes(breedSearch.toLowerCase()))
                      : currentStep.options;
                    return (
                      <div className={`mb-4 ${filteredOptions.length > 6 ? 'grid grid-cols-2 gap-2' : 'space-y-2.5'} max-h-[280px] overflow-y-auto pr-1`} style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(255,255,255,0.1) transparent',
                      }}>
                        {filteredOptions.map((opt, i) => (
                          <motion.button
                            key={opt.value}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.08 + i * 0.03 }}
                            whileHover={{ scale: 1.01, y: -1 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => { setTextInput(''); setBreedSearch(''); handleChoiceSelect(opt.value); }}
                            disabled={submitting}
                            className="w-full text-left px-4 py-3 rounded-xl border transition-all flex items-center justify-between bg-white/[0.04] border-white/10 hover:bg-white/[0.08] hover:border-white/20 group"
                            data-testid={`choice-${opt.value}`}
                          >
                            <span className="font-medium text-sm text-white/70 group-hover:text-white">{opt.label}</span>
                            <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-white/50 group-hover:translate-x-0.5 transition-all" />
                          </motion.button>
                        ))}
                        {breedSearch.trim() && filteredOptions.length === 0 && (
                          <p className="text-sm text-white/30 text-center py-4">
                            Nie znaleziono rasy. Wpisz ją poniżej.
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Separator */}
                  <div className="flex items-center gap-3 my-3">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-xs font-medium text-white/30">
                      {currentStep.breed_fallback ? 'nie ma na liście? wpisz ręcznie' : 'lub wpisz'}
                    </span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>

                  {/* Custom input + Dalej (only for custom text) */}
                  <div className="relative mb-4">
                    <input
                      ref={inputRef}
                      value={textInput}
                      onChange={(e) => { setTextInput(e.target.value); setSelectedChoice(null); }}
                      onKeyDown={(e) => e.key === 'Enter' && handleTextSubmit()}
                      placeholder={currentStep.custom_placeholder || 'Wpisz własną odpowiedź...'}
                      disabled={submitting}
                      className="w-full h-12 text-sm rounded-xl border border-white/10 bg-white/[0.04] px-4 pr-12 focus:border-white/25 focus:bg-white/[0.06] focus:outline-none focus:ring-0 text-white placeholder:text-white/25 transition-all"
                      data-testid="interview-custom-input"
                    />
                  </div>

                  {textInput.trim() && (
                    <Button
                      onClick={handleTextSubmit}
                      disabled={submitting}
                      className="h-12 px-8 rounded-xl font-semibold text-white transition-all"
                      style={{
                        background: accentColor,
                        boxShadow: `0 4px 16px ${accentColor}30`,
                      }}
                      data-testid="choice-or-custom-confirm"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                        <>Dalej <ArrowRight className="w-4 h-4 ml-2" /></>
                      )}
                    </Button>
                  )}
                </div>
              )}

              {/* ── TEXT WITH SUGGESTIONS TYPE ── */}
              {currentStep.type === 'text_with_suggestions' && (
                <>
                  {currentStep.suggestions?.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.15 }}
                      className="flex flex-wrap gap-2 mb-5"
                    >
                      {currentStep.suggestions.map((s, i) => (
                        <motion.button
                          key={s}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.1 + i * 0.03 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleSuggestionClick(s)}
                          className={`px-3.5 py-2 rounded-xl text-sm font-medium border transition-all ${
                            textInput === s
                              ? 'text-white border-transparent'
                              : 'bg-white/[0.04] text-white/50 border-white/10 hover:bg-white/[0.08] hover:border-white/20'
                          }`}
                          style={textInput === s ? {
                            background: accentColor,
                            boxShadow: `0 2px 8px ${accentColor}30`,
                          } : {}}
                          data-testid={`suggestion-${i}`}
                        >
                          {s}
                        </motion.button>
                      ))}
                    </motion.div>
                  )}

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="relative"
                  >
                    <input
                      ref={inputRef}
                      value={textInput}
                      onChange={(e) => setTextInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleTextSubmit()}
                      placeholder={currentStep.placeholder || 'Wpisz odpowiedź...'}
                      disabled={submitting}
                      className="w-full h-14 text-lg rounded-2xl border border-white/10 bg-white/[0.04] px-5 pr-14 focus:border-white/25 focus:bg-white/[0.06] focus:outline-none focus:ring-0 text-white placeholder:text-white/25 transition-all"
                      data-testid="interview-input"
                    />
                    <button
                      onClick={handleTextSubmit}
                      disabled={submitting || !textInput.trim()}
                      className="absolute right-2.5 top-2.5 w-9 h-9 rounded-xl flex items-center justify-center transition-all"
                      style={{
                        background: textInput.trim() ? accentColor : 'rgba(255,255,255,0.1)',
                        boxShadow: textInput.trim() ? `0 2px 8px ${accentColor}30` : 'none',
                      }}
                      data-testid="interview-submit-btn"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin text-white" />
                      ) : (
                        <ArrowRight className="w-4 h-4 text-white" />
                      )}
                    </button>
                  </motion.div>
                </>
              )}

              {/* ── TEXT TYPE (name input etc.) ── */}
              {currentStep.type === 'text' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="relative mb-4">
                    <input
                      ref={inputRef}
                      value={textInput}
                      onChange={(e) => setTextInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleTextSubmit()}
                      placeholder={currentStep.placeholder || 'Wpisz odpowiedź...'}
                      disabled={submitting}
                      className="w-full h-14 text-lg rounded-2xl border border-white/10 bg-white/[0.04] px-5 focus:border-white/25 focus:bg-white/[0.06] focus:outline-none focus:ring-0 text-white placeholder:text-white/25 transition-all"
                      data-testid="interview-input"
                    />
                    {textInput.trim() && (
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] text-white/25">Enter ↵</span>
                    )}
                  </div>
                  {textInput.trim() && (
                    <Button
                      onClick={handleTextSubmit}
                      disabled={submitting}
                      className="h-12 px-8 rounded-xl font-semibold text-white transition-all animate-pulse"
                      style={{
                        background: accentColor,
                        boxShadow: `0 4px 16px ${accentColor}30`,
                      }}
                      data-testid="text-confirm-btn"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                        <>Dalej <ArrowRight className="w-4 h-4 ml-2" /></>
                      )}
                    </Button>
                  )}
                </motion.div>
              )}

              {/* ── TEXTAREA TYPE ── */}
              {currentStep.type === 'textarea' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  {currentStep.strongly_recommended && (
                    <div className="flex items-center gap-2 mb-3 px-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#F47A3B] animate-pulse" />
                      <span className="text-[11px] font-medium text-[#F47A3B]/80">Mocno rekomendowane — pomoże tworzyć lepsze treści</span>
                    </div>
                  )}
                  <div className="relative mb-4">
                    <textarea
                      ref={inputRef}
                      value={textInput}
                      onChange={(e) => setTextInput(e.target.value)}
                      placeholder={currentStep.placeholder || 'Opisz swoimi słowami...'}
                      disabled={submitting}
                      rows={4}
                      className="w-full text-base rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4 focus:border-white/25 focus:bg-white/[0.06] focus:outline-none focus:ring-0 text-white placeholder:text-white/25 transition-all resize-none"
                      data-testid="interview-textarea"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={handleTextSubmit}
                      disabled={submitting}
                      className="h-12 px-8 rounded-xl font-semibold text-white transition-all"
                      style={{
                        background: textInput.trim() ? accentColor : 'rgba(255,255,255,0.08)',
                        boxShadow: textInput.trim() ? `0 4px 16px ${accentColor}30` : 'none',
                      }}
                      data-testid="textarea-confirm-btn"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                        <>{textInput.trim() ? 'Dalej' : 'Pomiń'} <ArrowRight className="w-4 h-4 ml-2" /></>
                      )}
                    </Button>
                    {textInput.length > 0 && (
                      <span className="text-[11px] text-white/25">{textInput.length} znaków</span>
                    )}
                  </div>
                </motion.div>
              )}

              {/* ── PHOTO UPLOAD TYPE with validation, progress, retry ── */}
              {currentStep.type === 'photo_upload' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  data-testid="interview-photo-upload"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ALLOWED_EXTENSIONS}
                    multiple
                    className="hidden"
                    onChange={(e) => handlePhotoUpload(e.target.files)}
                  />

                  {/* Upload area */}
                  <motion.button
                    whileHover={{ scale: 1.01, borderColor: 'rgba(255,255,255,0.25)' }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full py-10 rounded-2xl border-2 border-dashed border-white/15 bg-white/[0.03] hover:bg-white/[0.06] transition-all flex flex-col items-center gap-3 mb-4"
                    data-testid="photo-upload-area"
                  >
                    {uploading ? (
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="w-8 h-8 animate-spin" style={{ color: accentColor }} />
                        <span className="text-sm font-medium text-white/60">
                          Trwa przesyłanie... {uploadProgress > 0 ? `${uploadProgress}%` : ''}
                        </span>
                        {/* Progress bar */}
                        <div className="w-48 h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full rounded-full"
                            style={{ background: accentColor }}
                            initial={{ width: 0 }}
                            animate={{ width: `${uploadProgress}%` }}
                            transition={{ duration: 0.3 }}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${accentColor}20` }}>
                          <Upload className="w-6 h-6" style={{ color: accentColor }} />
                        </div>
                        <span className="text-sm font-medium text-white/50">
                          Kliknij, żeby dodać zdjęcia
                        </span>
                        <span className="text-xs text-white/25">JPG, PNG, WebP — max 5MB na zdjęcie</span>
                      </>
                    )}
                  </motion.button>

                  {/* Upload error with retry */}
                  {uploadError && !uploading && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-3 p-3 mb-4 rounded-xl bg-red-500/10 border border-red-500/20"
                      data-testid="upload-error"
                    >
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                      <p className="text-sm text-red-300 flex-1">{uploadError}</p>
                      <button
                        onClick={handlePhotoRetry}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs font-semibold transition-all flex-shrink-0"
                        data-testid="upload-retry-btn"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Spróbuj ponownie
                      </button>
                    </motion.div>
                  )}

                  {/* Thumbnails */}
                  {uploadedPhotos.length > 0 && (
                    <div className="flex gap-2 mb-4 flex-wrap">
                      {uploadedPhotos.map((url, i) => (
                        <div key={i} className="w-16 h-16 rounded-xl overflow-hidden border border-white/10">
                          <img src={`${API.replace('/api', '')}${url}`} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Submit photos */}
                  {uploadedPhotos.length > 0 && (
                    <Button
                      onClick={handlePhotoSubmit}
                      disabled={submitting}
                      className="h-12 px-8 rounded-xl font-semibold text-white transition-all"
                      style={{ background: accentColor, boxShadow: `0 4px 16px ${accentColor}30` }}
                      data-testid="photo-confirm-btn"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                        <>Dalej ({uploadedPhotos.length} {uploadedPhotos.length === 1 ? 'zdjęcie' : 'zdjęć'}) <ArrowRight className="w-4 h-4 ml-2" /></>
                      )}
                    </Button>
                  )}
                </motion.div>
              )}

              {/* ── SKIP BUTTON for optional steps ── */}
              {currentStep && currentStep.required === false && (
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  onClick={() => submitAnswer('pominięto')}
                  disabled={submitting}
                  className="mt-4 text-sm text-white/25 hover:text-white/50 transition-colors block mx-auto"
                  data-testid="skip-step-btn"
                >
                  Pomiń na teraz
                </motion.button>
              )}
            </motion.div>
          )}
          {phase === PHASE.PROCESSING && (
            <motion.div
              key="processing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="max-w-md w-full text-center"
              data-testid="onboarding-processing"
            >
              {/* Pulsing orb */}
              <div className="relative w-24 h-24 mx-auto mb-8">
                <motion.div
                  animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.1, 0.3] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className="absolute inset-0 rounded-full bg-[#F47A3B]"
                />
                <motion.div
                  animate={{ scale: [1, 1.15, 1] }}
                  transition={{ repeat: Infinity, duration: 2, delay: 0.2 }}
                  className="absolute inset-2 rounded-full bg-[#F47A3B] flex items-center justify-center shadow-xl shadow-orange-500/30"
                >
                  <Sparkles className="w-10 h-10 text-white" />
                </motion.div>
              </div>

              <h2 className="text-2xl font-extrabold text-white mb-2">
                Przygotowujemy Twoją promocję
              </h2>
              <p className="text-sm text-white/40 mb-10">To zajmie tylko chwilę...</p>

              <div className="space-y-3 text-left max-w-xs mx-auto">
                {PROCESSING_STEPS.map((step, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{
                      opacity: processingStep >= idx ? 1 : 0.25,
                      x: processingStep >= idx ? 0 : -15,
                    }}
                    transition={{ duration: 0.4 }}
                    className="flex items-center gap-3 py-2"
                  >
                    {processingStep > idx ? (
                      <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                    ) : processingStep === idx ? (
                      <Loader2 className="w-6 h-6 animate-spin text-[#F47A3B]" />
                    ) : (
                      <div className="w-6 h-6 rounded-full border-2 border-white/15" />
                    )}
                    <span className={`text-sm font-medium ${processingStep >= idx ? 'text-white/80' : 'text-white/20'}`}>
                      {step.text}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* ═══ RESULTS — Dashboard "Co dostales" ═══ */}
          {phase === PHASE.RESULTS && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl w-full"
              data-testid="onboarding-results"
            >
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-8"
              >
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-semibold mb-4">
                  <Check className="w-3.5 h-3.5" />
                  Wszystko gotowe!
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">
                  Oto co dla Ciebie przygotowaliśmy
                </h2>
                <p className="text-sm text-white/40">
                  Twoja strona, post i kanaly publikacji sa gotowe
                </p>
              </motion.div>

              {/* 1. TWOJA STRONA */}
              <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                className="rounded-2xl bg-white/[0.06] border border-emerald-500/20 overflow-hidden mb-4">
                <div className="flex items-center gap-3 px-5 py-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-bold text-sm">Twoja strona jest gotowa</h3>
                    <p className="text-white/40 text-xs mt-0.5">Klienci zobacza tu Twoja oferte, zdjecia i formularz kontaktowy</p>
                  </div>
                  <button onClick={() => navigate('/panel/strona')} className="px-4 py-2 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/20 text-emerald-300 text-xs font-semibold transition-all flex items-center gap-1.5">
                    Zobacz <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
              </motion.div>

              {/* 2. TWOJ POST */}
              <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                className="rounded-2xl bg-white/[0.06] border border-white/10 overflow-hidden mb-4">
                <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
                  <div className="w-10 h-10 rounded-xl bg-[#F47A3B]/15 flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-[#F47A3B]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-bold text-sm">Twój post jest gotowy</h3>
                    <p className="text-white/40 text-xs mt-0.5">Tekst reklamowy stworzony na podstawie Twojego wywiadu</p>
                  </div>
                </div>
                <div className="px-5 py-3">
                  {firstPost?.text ? (
                    <p className="text-white/70 text-sm leading-relaxed whitespace-pre-wrap line-clamp-6">{firstPost.text}</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="h-3 bg-white/10 rounded w-full animate-pulse" />
                      <div className="h-3 bg-white/10 rounded w-4/5 animate-pulse" />
                    </div>
                  )}
                  {firstPost?.photo && (
                    <img src={firstPost.photo} alt="" className="w-full h-32 object-cover rounded-xl mt-3 border border-white/10" />
                  )}
                </div>
                <div className="px-5 pb-3">
                  <button onClick={copyPostText} className="flex items-center gap-2 text-xs font-medium text-white/40 hover:text-white/70 transition-colors" data-testid="copy-post-btn">
                    {postCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    {postCopied ? 'Skopiowano!' : 'Kopiuj tekst'}
                  </button>
                </div>
              </motion.div>

              {/* 3. GDZIE OPUBLIKOWAC — kanaly */}
              <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
                className="rounded-2xl bg-white/[0.06] border border-white/10 overflow-hidden mb-6">
                <div className="px-5 py-4 border-b border-white/5">
                  <h3 className="text-white font-bold text-sm">Gdzie opublikowac?</h3>
                  <p className="text-white/40 text-xs mt-0.5">Wybierz kanal i wrzuc swoj post — to trwa 30 sekund</p>
                </div>

                {/* Grupy FB */}
                <div className="px-5 py-3 border-b border-white/5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center">
                      <Users className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <span className="text-white/90 text-sm font-semibold">Grupy na Facebooku</span>
                      <span className="text-emerald-400 text-[10px] font-bold ml-2">NAJSKUTECZNIEJSZE</span>
                    </div>
                  </div>
                  <p className="text-white/30 text-xs mb-2 ml-11">Dołącz do grupy → wklej tekst (Ctrl+V) → dodaj zdjęcie → opublikuj</p>
                  {recommendedGroups.length > 0 && (
                    <div className="space-y-1.5 ml-11">
                      {recommendedGroups.slice(0, 3).map((group, i) => (
                        <a key={i} href={group.url} target="_blank" rel="noopener noreferrer"
                          onClick={() => { if (firstPost?.text) navigator.clipboard.writeText(firstPost.text); toast.success('Tekst skopiowany! Wklej na FB'); }}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.04] hover:bg-blue-500/10 border border-white/5 hover:border-blue-400/20 transition-all text-xs"
                          data-testid={`channel-fb-group-${i}`}>
                          <span className="text-white/70 flex-1 truncate">{group.name}</span>
                          <span className="text-[#F47A3B] text-[10px] font-bold">{group.members} czl.</span>
                          <ExternalLink className="w-3 h-3 text-blue-400" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                {/* OLX */}
                <a href="https://www.olx.pl/mojolx/nowe-ogloszenie/" target="_blank" rel="noopener noreferrer"
                  onClick={() => { if (firstPost?.text) navigator.clipboard.writeText(firstPost.text); toast.success('Tekst skopiowany! Wklej na OLX'); }}
                  className="flex items-center gap-3 px-5 py-3 border-b border-white/5 hover:bg-white/[0.04] transition-colors group"
                  data-testid="channel-olx">
                  <div className="w-8 h-8 rounded-lg bg-green-500/15 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <span className="text-white/90 text-sm font-semibold group-hover:text-green-400 transition-colors">OLX.pl</span>
                    <p className="text-white/30 text-xs">Dodaj ogłoszenie — tekst skopiuje się automatycznie</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-white/20 group-hover:text-green-400" />
                </a>

                {/* WhatsApp */}
                <a href={`https://wa.me/?text=${encodeURIComponent(firstPost?.text || '')}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 px-5 py-3 border-b border-white/5 hover:bg-white/[0.04] transition-colors group"
                  data-testid="channel-whatsapp">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                    <Send className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <span className="text-white/90 text-sm font-semibold group-hover:text-emerald-400 transition-colors">WhatsApp</span>
                    <p className="text-white/30 text-xs">Wyślij post do znajomych lub na status</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-white/20 group-hover:text-emerald-400" />
                </a>

                {/* Email */}
                <a href={`mailto:?subject=${encodeURIComponent('Sprawdź moją ofertę')}&body=${encodeURIComponent(firstPost?.text || '')}`}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-white/[0.04] transition-colors group"
                  data-testid="channel-email">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/15 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <span className="text-white/90 text-sm font-semibold group-hover:text-purple-400 transition-colors">Email</span>
                    <p className="text-white/30 text-xs">Wyślij ofertę mailem do swoich kontaktów</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-white/20 group-hover:text-purple-400" />
                </a>
              </motion.div>

              {/* CO TERAZ — 3 kroki */}
              <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
                className="rounded-2xl bg-white/[0.04] border border-white/8 p-5 mb-8">
                <h3 className="text-white font-bold text-sm mb-3">Co teraz? 3 proste kroki</h3>
                <div className="space-y-3">
                  {[
                    { num: '1', text: 'Dołącz do grup na Facebooku (kliknij powyżej)', color: 'bg-blue-500' },
                    { num: '2', text: 'Wklej tekst posta (kopiuje się automatycznie)', color: 'bg-[#F47A3B]' },
                    { num: '3', text: 'Czekaj na wiadomości — trafią do Twojego panelu + WhatsApp', color: 'bg-emerald-500' },
                  ].map((step, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-full ${step.color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>{step.num}</div>
                      <p className="text-white/70 text-sm">{step.text}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* CTA */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
                className="text-center">
                <Button
                  onClick={() => navigate('/panel')}
                  className="h-14 px-10 text-lg font-bold rounded-2xl text-white bg-[#F47A3B] hover:bg-[#E06A2E] shadow-xl shadow-orange-500/25 hover:shadow-2xl transition-all group w-full sm:w-auto"
                  data-testid="results-go-panel-btn">
                  Przejdz do panelu
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <p className="mt-3 text-xs text-white/20">W panelu znajdziesz więcej postów, statystyki i ustawienia</p>
              </motion.div>
            </motion.div>
          )}

        </AnimatePresence>
      </main>
    </div>
  );
}
